import { mediaQueryLarge, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Initialize layout state immediately on load
    this.#initLayout();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Initializes layout from sessionStorage or fallback to HTML default.
   * Priority: sessionStorage > HTML checked attribute.
   * Note: sessionStorage is ignored in Shopify Design Mode.
   */
  #initLayout() {
    const isDesktop = mediaQueryLarge.matches;
    const viewport = isDesktop ? 'desktop' : 'mobile';
    const inputName = isDesktop ? 'grid' : 'grid-mobile';
    const isDesignMode = window.Shopify?.designMode;

    let savedLayout = null;

    // 1. Check browser storage first (only if NOT in Design Mode)
    if (!isDesignMode) {
      savedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`);
    }

    // 2. Check what is rendered by Liquid (theme settings)
    const checkedHtmlInput = this.querySelector(`input[name="${inputName}"]:checked`);
    const defaultLayout = checkedHtmlInput ? checkedHtmlInput.value : 'default';

    // 3. Determine final layout: Storage wins if it exists and not in editor, otherwise use Liquid default
    const finalLayout = savedLayout || defaultLayout;

    // 4. Apply the visual layout
    this.#setLayout(finalLayout);

    // 5. Force update the radio button state to match the logic
    const inputToSelect = this.querySelector(`input[name="${inputName}"][value="${finalLayout}"]`);
    if (inputToSelect && inputToSelect instanceof HTMLInputElement) {
      inputToSelect.checked = true;
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout and saves the preference.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    const isDesignMode = window.Shopify?.designMode;

    // Save immediately to storage (only if NOT in Design Mode)
    if (!isDesignMode) {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    }

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);
  };

  /**
   * Applies the layout attribute to the grid.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    // Re-initialize layout when switching between desktop and mobile
    this.#initLayout();
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}